import { useState, useEffect, useRef, useCallback } from "react";
import { kickRepaint } from "@/lib/repaint";
import { useQuery } from "@tanstack/react-query";
import {
  X, SkipForward, SkipBack, Television, SpinnerGap, Shuffle,
  SpeakerHigh, SpeakerSlash, FilmReel, FilmSlate, Stack, PushPin,
} from "@phosphor-icons/react";
import { fetchRetroShuffle } from "@/lib/api";
import { useIdleCursor } from "@/components/SpatialNav";

// Pin-channel lanes — "all" surfs everything, else lock to one retro category.
const CATEGORIES = [
  { id: "all", label: "All", Icon: Stack },
  { id: "cartoons", label: "Cartoons", Icon: FilmReel },
  { id: "classic", label: "Classics", Icon: FilmSlate },
];

// Builds an autoplay embed URL for the retro channel feed.
const retroEmbed = (url) => {
  if (!url) return "";
  if (/youtube\.com\/embed/.test(url)) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  }
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}autoplay=1`;
};

// Shared AudioContext for all retro sound effects.
const useAudioFx = () => {
  const ctxRef = useRef(null);
  const getCtx = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      if (!ctxRef.current) ctxRef.current = new Ctx();
      if (ctxRef.current.state === "suspended") ctxRef.current.resume();
      return ctxRef.current;
    } catch (e) {
      return null;
    }
  }, []);

  // White-noise burst for the channel-change static.
  const playStatic = useCallback((ms = 650) => {
    const ctx = getCtx();
    if (!ctx) return;
    const frames = Math.floor(ctx.sampleRate * (ms / 1000));
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const out = buffer.getChannelData(0);
    for (let i = 0; i < frames; i += 1) out[i] = (Math.random() * 2 - 1) * 0.5;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.16, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + ms / 1000);
    src.connect(gain).connect(ctx.destination);
    src.start();
    src.stop(ctx.currentTime + ms / 1000);
  }, [getCtx]);

  // Vintage CRT power-on: a low degauss "thunk" + the high flyback whine that fades in.
  const playBoot = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;
    const t = ctx.currentTime;
    // Degauss thump — quick low sine sweep.
    const thump = ctx.createOscillator();
    const tg = ctx.createGain();
    thump.type = "sine";
    thump.frequency.setValueAtTime(120, t);
    thump.frequency.exponentialRampToValueAtTime(42, t + 0.22);
    tg.gain.setValueAtTime(0.0001, t);
    tg.gain.exponentialRampToValueAtTime(0.32, t + 0.02);
    tg.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    thump.connect(tg).connect(ctx.destination);
    thump.start(t);
    thump.stop(t + 0.4);
    // Flyback whine — ~15.7kHz tone, softly present then gone (classic CRT hum).
    const whine = ctx.createOscillator();
    const wg = ctx.createGain();
    whine.type = "triangle";
    whine.frequency.setValueAtTime(15700, t + 0.08);
    wg.gain.setValueAtTime(0.0001, t + 0.08);
    wg.gain.exponentialRampToValueAtTime(0.05, t + 0.2);
    wg.gain.exponentialRampToValueAtTime(0.0008, t + 1.1);
    whine.connect(wg).connect(ctx.destination);
    whine.start(t + 0.08);
    whine.stop(t + 1.2);
  }, [getCtx]);

  return { playStatic, playBoot };
};

export const RetroPlayer = ({ onClose }) => {
  const [category, setCategory] = useState("all");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["retro-shuffle", category],
    queryFn: () => fetchRetroShuffle(60, category),
    staleTime: 0,
  });

  const items = data?.items ?? [];
  const [index, setIndex] = useState(0);
  const [showStatic, setShowStatic] = useState(false);
  const [muted, setMuted] = useState(false);
  const [bleed, setBleed] = useState(false);
  const [dialKey, setDialKey] = useState(0);
  const mutedRef = useRef(false);
  const { playStatic, playBoot } = useAudioFx();
  const advanceTimer = useRef(null);
  const staticTimer = useRef(null);
  const rootRef = useRef(null);
  const booted = useRef(false);

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const current = items[index];

  // CRT power-on boot sound — once, on first open (unless muted).
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    if (!mutedRef.current) playBoot();
  }, [playBoot]);

  // Every channel landing fires a UHF dial-number flash + an RGB color-bleed glitch so
  // rapid surfing feels like spinning an old tuner.
  useEffect(() => {
    if (!items.length) return undefined;
    setBleed(true);
    setDialKey((k) => k + 1);
    const t = setTimeout(() => setBleed(false), 520);
    return () => clearTimeout(t);
  }, [index, category, items.length]);

  // Channel-change transition: snow + static burst, then swap to the next/prev title.
  const changeTo = useCallback(
    (nextIndex) => {
      if (!items.length) return;
      const wrapped = ((nextIndex % items.length) + items.length) % items.length;
      setShowStatic(true);
      if (!mutedRef.current) playStatic(650);
      clearTimeout(staticTimer.current);
      staticTimer.current = setTimeout(() => {
        setIndex(wrapped);
        setShowStatic(false);
      }, 650);
    },
    [items.length, playStatic]
  );

  const next = useCallback(() => changeTo(index + 1), [changeTo, index]);
  const prev = useCallback(() => changeTo(index - 1), [changeTo, index]);
  const randomJump = useCallback(() => {
    if (items.length < 2) return;
    let r = index;
    while (r === index) r = Math.floor(Math.random() * items.length);
    changeTo(r);
  }, [changeTo, index, items.length]);

  // Pin a category: reset to the top of the new lane with a static transition.
  const pinCategory = useCallback(
    (cat) => {
      if (cat === category) return;
      setShowStatic(true);
      if (!mutedRef.current) playStatic(650);
      setCategory(cat);
      setIndex(0);
      clearTimeout(staticTimer.current);
      staticTimer.current = setTimeout(() => setShowStatic(false), 650);
    },
    [category, playStatic]
  );

  // Endless auto-shuffle: advance when the current title's runtime elapses (capped).
  useEffect(() => {
    if (!current || showStatic) return undefined;
    const dur = current.duration_sec || 0;
    const ms = Math.min(Math.max(dur > 0 ? dur : 300, 45), 1800) * 1000;
    advanceTimer.current = setTimeout(() => next(), ms);
    return () => clearTimeout(advanceTimer.current);
  }, [current, showStatic, next]);

  useIdleCursor(true);

  // Up/Down surf channels, Left/Right move focus across the on-screen controls, Esc exits.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowUp") { e.preventDefault(); next(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const btns = Array.from(rootRef.current?.querySelectorAll("button:not([disabled])") || []);
        if (!btns.length) return;
        const i = btns.indexOf(document.activeElement);
        const step = e.key === "ArrowRight" ? 1 : -1;
        const target = i === -1 ? btns[0] : btns[(i + step + btns.length) % btns.length];
        target.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  useEffect(() => () => {
    clearTimeout(advanceTimer.current);
    clearTimeout(staticTimer.current);
  }, []);

  const channelNo = String((index % 99) + 2).padStart(2, "0");

  return (
    <div ref={rootRef} data-testid="retro-player" data-spatial-trap="" className="rv-retro fixed inset-0 z-[90] bg-black flex flex-col">
      {/* Top bar */}
      <div className="relative z-30 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-amber/20 bg-black/80 backdrop-blur-md">
        <div className="flex items-center gap-2.5 shrink-0">
          <Television size={22} weight="duotone" className="text-amber" />
          <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber">Retro Mode</span>
        </div>

        {/* Pin Channel — lock the surf to one retro lane */}
        <div data-testid="retro-pin-group" className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] p-1">
          <PushPin size={13} weight="fill" className="ml-1.5 mr-0.5 text-white/35" />
          {CATEGORIES.map(({ id, label, Icon }) => (
            <button
              key={id}
              data-testid={`retro-pin-${id}`}
              onClick={() => pinCategory(id)}
              aria-pressed={category === id}
              className={`flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1.5 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                category === id
                  ? "bg-amber text-black"
                  : "text-white/60 hover:text-amber"
              }`}
            >
              <Icon size={13} weight="duotone" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="retro-sound-toggle"
            onClick={() => setMuted((m) => !m)}
            title={muted ? "Unmute retro sounds" : "Mute retro sounds"}
            aria-pressed={!muted}
            className={`flex items-center justify-center rounded-full border p-2 transition-colors duration-300 ${
              muted ? "border-white/15 text-white/50 hover:text-white" : "border-amber/50 text-amber"
            }`}
          >
            {muted ? <SpeakerSlash size={16} weight="duotone" /> : <SpeakerHigh size={16} weight="duotone" />}
          </button>
          <button
            data-testid="retro-close-btn"
            onClick={onClose}
            className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 hover:border-crimson hover:text-crimson transition-colors duration-300"
          >
            <X size={14} weight="bold" /> Exit
          </button>
        </div>
      </div>

      {/* Screen area */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden p-2 sm:p-6">
        <div className={`relative aspect-video w-full max-w-6xl bg-black overflow-hidden crt-screen rounded-[14px] border border-white/10 ${bleed ? "crt-bleed" : ""}`}>
          {isLoading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/60">
              <SpinnerGap size={34} className="animate-spin text-amber" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Warming up the tubes…</span>
            </div>
          ) : isError || items.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="retro-empty">
              <Television size={40} weight="light" className="text-white/25" />
              <p className="font-serif text-xl text-white/70">No signal</p>
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/35">
                No titles on this channel yet — try another lane or ingest more retro content.
              </p>
            </div>
          ) : (
            <>
              <iframe
                key={current?.video_id}
                data-testid="retro-iframe"
                src={retroEmbed(current?.monetized_embed_url)}
                title={current?.title || "Retro channel"}
                onLoad={(e) => kickRepaint(e.currentTarget)}
                className="rv-gpu absolute inset-0 z-[5] h-full w-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              <div className="pointer-events-none absolute inset-0 crt-scanlines rv-crt-overlay z-10" />
              <div className="pointer-events-none absolute inset-0 crt-roll rv-crt-overlay z-10" />
              <div className="pointer-events-none absolute inset-0 crt-vignette rv-crt-overlay z-20" />

              {/* RGB color-bleed sweep on channel change */}
              {bleed && <div data-testid="retro-bleed" className="pointer-events-none absolute inset-0 z-[25] crt-bleed-overlay" />}

              {/* UHF dial-number flash */}
              <div
                key={dialKey}
                data-testid="retro-dial"
                className="crt-dial pointer-events-none absolute top-3 right-4 z-30 font-mono text-amber text-3xl sm:text-6xl font-bold tracking-widest drop-shadow-[0_0_14px_rgba(245,158,11,0.8)]"
              >
                CH {channelNo}
              </div>

              {showStatic && (
                <div data-testid="retro-static" className="absolute inset-0 z-40 crt-static flex items-center justify-center">
                  <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/80 bg-black/40 px-3 py-1">·· tuning ··</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Now-playing + controls */}
      <div className="relative z-30 border-t border-amber/20 bg-black/85 backdrop-blur-md px-4 sm:px-6 py-3.5">
        <div className="mx-auto max-w-6xl flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber/70">
              Now Playing{category !== "all" ? ` · ${CATEGORIES.find((c) => c.id === category)?.label} pinned` : ""}
            </p>
            <p data-testid="retro-now-playing" className="truncate font-serif text-base sm:text-lg text-white/90">
              {current?.title || "—"}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="retro-prev-btn"
              onClick={prev}
              title="Previous channel"
              className="flex items-center justify-center rounded-full border border-white/15 p-2.5 text-white/80 hover:border-amber hover:text-amber transition-colors duration-300"
            >
              <SkipBack size={18} weight="fill" />
            </button>
            <button
              data-testid="retro-shuffle-btn"
              onClick={randomJump}
              title="Shuffle to a random channel"
              className="flex items-center justify-center rounded-full border border-white/15 p-2.5 text-white/80 hover:border-amber hover:text-amber transition-colors duration-300"
            >
              <Shuffle size={18} weight="bold" />
            </button>
            <button
              data-testid="retro-next-btn"
              onClick={next}
              title="Next channel"
              className="flex items-center gap-2 rounded-full bg-amber px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-black font-semibold hover:bg-crimson hover:text-white transition-colors duration-300"
            >
              <SkipForward size={16} weight="fill" /> Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
